import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Eyebrow, StatusChip } from "@/components/Type";
import { PILLARS, pillarById } from "@/content/pillars";
import { programmeById } from "@/content/programmes";
import { productById } from "@/content/products";
import { articleBySlug } from "@/content/articles";

function PillarSection({ p }) {
  const [open, setOpen] = useState(false);
  return (
    <section id={p.id} className="scroll-mt-24 border-t border-[#1f1e1a]">
      <button
        type="button"
        aria-expanded={open}
        data-testid={`pillar-row-${p.id}`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-6 py-8 text-left"
      >
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <span className="font-mono text-[10px] tracking-[0.3em] text-[#C9A227]">
            {p.number} / 08
          </span>
          <span className="font-display text-2xl font-extrabold tracking-tight text-[#f5f2ea] sm:text-3xl">
            {p.name}
          </span>
        </div>
        <span className="font-mono text-lg text-[#57534a]" aria-hidden="true">
          {open ? "—" : "+"}
        </span>
      </button>

      {open && (
        <div className="fade-up grid gap-10 pb-12 lg:grid-cols-2">
          <div>
            <p className="font-display text-lg font-bold text-[#b8b3a6]">{p.short}</p>
            <p className="mt-4 text-sm leading-relaxed text-[#8e8a80]">{p.long}</p>
            <p className="mt-8 font-mono text-[9px] tracking-[0.3em] text-[#57534a]">WHY IT MATTERS</p>
            <p className="mt-2 text-sm leading-relaxed text-[#8e8a80]">{p.why}</p>
          </div>
          <div>
            <p className="font-mono text-[9px] tracking-[0.3em] text-[#57534a]">W8ED PERSPECTIVE</p>
            <p className="mt-2 text-sm leading-relaxed text-[#8e8a80]">{p.perspective}</p>

            <p className="mt-8 font-mono text-[9px] tracking-[0.3em] text-[#57534a]">CONNECTED TO</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {p.related.map((id) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="border border-[#26241d] px-3 py-1.5 font-mono text-[9px] tracking-[0.2em] text-[#8e8a80] transition-colors hover:border-[#C9A227] hover:text-[#C9A227]"
                >
                  {pillarById(id)?.name || id}
                </a>
              ))}
            </div>

            <p className="mt-8 font-mono text-[9px] tracking-[0.3em] text-[#57534a]">RESEARCH</p>
            <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-[#57534a]">{p.research}</p>

            <p className="mt-8 font-mono text-[9px] tracking-[0.3em] text-[#57534a]">IN THE SYSTEM</p>
            <ul className="mt-3 space-y-2">
              {p.thinking.map((slug) => (
                <li key={slug}>
                  <Link to={`/thinking/${slug}`} className="font-mono text-[10px] tracking-[0.15em] text-[#8e8a80] hover:text-[#C9A227] transition-colors">
                    THINKING — {articleBySlug(slug)?.title || slug}
                  </Link>
                </li>
              ))}
              {p.programmes.map((id) => (
                <li key={id}>
                  <Link to={`/programmes#${id}`} className="font-mono text-[10px] tracking-[0.15em] text-[#8e8a80] hover:text-[#C9A227] transition-colors">
                    PROGRAMME — {programmeById(id)?.name || id}
                  </Link>
                </li>
              ))}
              {p.products.map((id) => (
                <li key={id}>
                  <Link to={`/products#${id}`} className="font-mono text-[10px] tracking-[0.15em] text-[#8e8a80] hover:text-[#C9A227] transition-colors">
                    PRODUCT — {productById(id)?.name || id}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <StatusChip status={p.status} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default function SciencePage() {
  return (
    <>
      <Seo
        title="Science — The 8 Pillars | W8ED"
        description="Human health is a system of signals, behaviours, inputs, outputs and adaptations that change over time. Explore the eight W8ED pillars."
        path="/science"
      />
      <section className="px-6 pb-20 pt-32 sm:pt-40">
        <div className="mx-auto max-w-6xl">
          <Eyebrow>Science</Eyebrow>
          <h1 className="mt-8 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#f5f2ea] sm:text-6xl">
            THE BODY IS A SYSTEM.
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#8e8a80]">
            Human health is not a collection of isolated variables. It is a system of signals,
            behaviours, inputs, outputs and adaptations that change over time.
          </p>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#8e8a80]">
            These are the eight pillars. Time is not one of them — time is what they compound
            through.
          </p>
        </div>
      </section>

      <div className="px-6 pb-24">
        <div className="mx-auto max-w-6xl border-b border-[#1f1e1a]">
          {PILLARS.map((p) => (
            <PillarSection key={p.id} p={p} />
          ))}
        </div>
      </div>
    </>
  );
}
